import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, type FormEvent, type MouseEvent } from 'react'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import {
  addCustomKit,
  addCustomPackage,
  addKit,
  applicationsPageQuery,
  applicationsTableQuery,
  assignDevicesLicense,
  changeApplicationCategory,
  changeApplicationTag,
  customKitsQuery,
  customPackagesQuery,
  deleteCustomKit,
  deleteCustomPackage,
  isSoftseeTab,
  kitsQuery,
  licenseDevicesAvailableQuery,
  licenseDevicesQuery,
  licenseFormQuery,
  licensesQuery,
  licensesTableQuery,
  newCategory,
  newLicense,
  osLabel,
  removeDeviceLicense,
  removeKit,
  removeLicense,
  softseeShellQuery,
  taggingTableQuery,
  updateLicense,
  type SoftseeTab,
} from '../../api/settingsSoftsee'
import { DatePicker } from '../../ui/DatePicker'
import { Modal } from '../../ui/Modal'
import { newAlert, toast } from '../../ui/notify'
import { SelectPicker } from '../../ui/SelectPicker'
import { SettingsHead, SettingsPicker, SettingsPickerItem } from '../../ui/SettingsParts'
import { Spinner } from '../../ui/Spinner'
import { ProceduresPage } from '../../softsee/ProceduresPage'
import { SoftseeListChrome } from './SoftseeListChrome'
import { EditCustomKitModal, EditCustomPackageModal, EditKitModal } from './SoftseeKitEditModals'
import { CredentialsPanel, DiscoverySetupPanel, ScanRulesPanel, TelemetryPanel } from './SoftseeDiscoveryPanels'
import { LibraryScansPanel } from './LibraryScansPanel'
import { RestartPolicyPanel } from './RestartPolicyPanel'
import { KevTicketsPanel } from './KevTicketsPanel'
import { CveDatabasePanel } from './CveDatabasePanel'

/**
 * Settings → Softsee: tabList.php + fiecare ecran din
 * softsee_settings/, element cu element. Discovery e pe aceleasi tab-uri.
 */

export function SoftseeSettingsPage({ tab, openAssistant = false }: { tab: string; openAssistant?: boolean }) {
  const navigate = useNavigate()
  const shell = useQuery(softseeShellQuery())
  /* Ce a deschis omul cu mana, peste ce spune ecranul pe care sta. `null` =
     n-a atins nimeni capul, deci hotaraste tabul curent. Sta inaintea
     iesirilor de mai jos: un hook nu se sare. */
  const [discoveryOpen, setDiscoveryOpen] = useState<boolean | null>(null)

  useEffect(() => {
    if (!shell.data) return
    if (tab !== '' && isSoftseeTab(tab)) return
    const first = shell.data.tabs.find((t) => !t.external)
    if (first) void navigate({ to: '/settings/softsee/$tab', params: { tab: first.id } })
  }, [shell.data, tab, navigate])

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!shell.data) return <Spinner />

  const current = isSoftseeTab(tab) ? tab : (shell.data.tabs.find((t) => !t.external)?.id ?? '')
  const local = shell.data.tabs.filter((t) => !t.group)
  const discovery = shell.data.tabs.filter((t) => t.group === 'Discovery')
  const discoveryShown = discoveryOpen ?? discovery.some((t) => t.id === current)

  return (
    <>
      <aside className="od-dark-sidebar od-module-rail">
        <ul className="od-dark-sidebar-nav default-sidebar-nav">
          <li className="menu-header">Softsee</li>
          {local.map((item) => (
            <SidebarTab key={item.id} tab={item} current={current} />
          ))}
          {discovery.length > 0 && (
            /* `active` pe <li> si `display` pe <ul>, amandoua: `app.css` ascunde
               orice `ul` din rail (`.od-dark-sidebar-nav ul`) si il arata
               inapoi numai sub `li.active`, iar stilul scris in element bate
               si regula aia. Inainte <li> nu primea niciodata `active` si
               <ul> purta `display: 'none'` batut in cuie, deci cele patru
               ecrane erau in pagina si nu se vedeau niciodata. La fel ca la
               capul de submeniu din meniul Easeedesk. */
            <li className={discoveryShown ? 'submenu-header active' : 'submenu-header'}>
              <a
                href="#"
                aria-expanded={discoveryShown}
                onClick={(event) => {
                  event.preventDefault()
                  setDiscoveryOpen(!discoveryShown)
                }}
              >
                <i className="fas fa-tasks" aria-hidden="true"></i> Discovery
              </a>
              <ul style={{ display: discoveryShown ? 'block' : 'none' }}>
                {discovery.map((item) => (
                  <SidebarTab key={item.id} tab={item} current={current} />
                ))}
              </ul>
            </li>
          )}
          <li className="menu-block-divider"></li>
        </ul>
      </aside>
      <div className="page-content-wrapper">
        {current === 'manage_applications' && <ApplicationsPanel />}
        {current === 'softwares_tagging' && <TaggingPanel />}
        {current === 'softwares_licenses' && <LicensesPanel />}
        {current === 'manage_kits' && <KitsPanel />}
        {current === 'manage_custom_kits' && <CustomKitsPanel />}
        {current === 'manage_custom_packages' && <CustomPackagesPanel />}
        {current === 'procedures' && <ProceduresPage />}
        {current === 'library_scans' && <LibraryScansPanel />}
        {current === 'restart_policy' && <RestartPolicyPanel />}
        {current === 'kev_tickets' && <KevTicketsPanel />}
        {current === 'discovery_setup' && <DiscoverySetupPanel openAssistant={openAssistant} />}
        {current === 'telemetry' && <TelemetryPanel />}
        {current === 'credentials' && <CredentialsPanel />}
        {current === 'scan_rules' && <ScanRulesPanel />}
        {current === 'cve_database' && <CveDatabasePanel />}
      </div>
    </>
  )
}

function SidebarTab({ tab, current }: { tab: SoftseeTab; current: string }) {
  const navigate = useNavigate()
  const active = tab.id === current
  const href = tab.external ? '/' + tab.href.replace(/^\//, '') : '/app/settings/softsee/' + tab.id
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (tab.external) return
    event.preventDefault()
    void navigate({ to: '/settings/softsee/$tab', params: { tab: tab.id } })
  }
  return (
    <li className={active ? 'menu-tab active' : 'menu-tab'}>
      <a id={tab.anchor} title={tab.title} href={href} data-toggle={tab.external ? 'tab' : undefined} onClick={onClick}>
        {tab.group ? null : <i className={tab.icon} aria-hidden="true"></i>} {tab.label}
      </a>
    </li>
  )
}

function num(row: Record<string, unknown>, key: string): number {
  return Number(row[key] ?? 0)
}

function str(row: Record<string, unknown>, key: string): string {
  return String(row[key] ?? '')
}

/*
 * `BsModal` s-a sters aici. Era fereastra scrisa de mana a fisierului asta, si
 * punea doar `display: block` pe `.modal`: se deschidea fara valul din spate,
 * fara `body.modal-open` - pagina de dedesubt ramanea de derulat - si fara
 * Escape. Cele trei ecrane care o mai tineau (Manage Kits, Manage Custom Kits,
 * Manage Custom Packages) au trecut pe `Modal`, cea comuna a zonei, deci nu o
 * mai cheama nimeni.
 */

function ApplicationsPanel() {
  const query = useQueryClient()
  const [appType, setAppType] = useState(-1)
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const page = useQuery(applicationsPageQuery())
  /* `keepPreviousData`: pagina de acum ramane pe ecran pana vine urmatoarea. */
  const table = useQuery({
    ...applicationsTableQuery({ appType, start, length, search }),
    placeholderData: keepPreviousData,
  })
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const onSave = async () => {
    try {
      await newCategory(name)
      toast('Category saved.', '', 'success')
      setOpen(false)
      setName('')
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'applications'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const onChange = async (application_id: number, category: number) => {
    try {
      await changeApplicationCategory(application_id, category)
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'applications'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const categoryOptions = [
    { value: '0', label: 'Select category...' },
    ...page.data.categories.map((cat) => ({ value: String(num(cat, 'ID')), label: str(cat, 'category_name') })),
  ]
  /* O categorie noua din coloana reia lista de la prima pagina. */
  const pick = (next: number) => {
    setAppType(next)
    setStart(0)
  }
  const rows = table.data?.data ?? []
  return (
    <>
      <div id="content">
        {/*
         * Capul zonei: `h4` + `label.color`, cu unealta plutita la dreapta
         * INAINTEA titlului. Inainte era `header.panel-heading` cu `h3`, iar
         * "Add category" statea `btn-block` deasupra coloanei din stanga, adica
         * chiar peste lista - acelasi lucru care s-a indreptat pe Inventoree ›
         * Software Licenses.
         */}
        <SettingsHead
          strong="Manage"
          rest="software"
          subtitle="software management system"
          tools={
            <button type="button" id="odAddCategory" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add category
            </button>
          }
        />
        <div className="row">
          <div className="col-md-3">
            {/* Coloana de alegere a consolei, ca pe Inventoree › Alarms si Software Licenses. */}
            <SettingsPicker id="categories-list">
              <SettingsPickerItem
                label="No category"
                active={appType === 0}
                onPick={() => pick(0)}
                liProps={{ 'data-category': 0 }}
              />
              <SettingsPickerItem
                label="All categories"
                active={appType === -1}
                onPick={() => pick(-1)}
                liProps={{ 'data-category': -1 }}
              />
              {page.data.categories.map((cat) => (
                <SettingsPickerItem
                  key={num(cat, 'ID')}
                  label={str(cat, 'category_name')}
                  active={appType === num(cat, 'ID')}
                  onPick={() => pick(num(cat, 'ID'))}
                  liProps={{ 'data-category': num(cat, 'ID'), className: 'category-tab' }}
                />
              ))}
            </SettingsPicker>
          </div>
          <div className="col-md-9">
            {/*
             * Catalogul vine PE PAGINI: categoria din coloana, cautarea si
             * pagina pleaca la server. Inainte ecranul cerea tot catalogul si il
             * taia in browser - pe o firma cu 20.000 de aplicatii insemna un
             * raspuns de 57 MB si 20.000 de randuri desenate deodata, fiecare cu
             * sertarul lui.
             */}
            <SoftseeListChrome
              id="applications-tagging-table"
              search={search}
              onSearch={(next) => {
                setSearch(next)
                setStart(0)
              }}
              start={start}
              length={length}
              total={table.data?.recordsFiltered ?? 0}
              fetching={table.isFetching}
              onStart={setStart}
              onLength={(next) => {
                setLength(next)
                setStart(0)
              }}
            >
              <table id="applications-tagging-table" className="table table-striped table-hover table-bordered dataTableM dataTable no-footer applications-tagging-table">
                {/* Coloanele au latimea lor, deci categoria si sertarul stau la acelasi x pe toate randurile. */}
                <colgroup>
                  <col />
                  <col style={{ width: 220 }} />
                  <col style={{ width: 260 }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className="td-small a-left">Application</th>
                    <th className="td-small a-left">Category</th>
                    <th className="td-small a-left">Change category</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr className="odd">
                      <td colSpan={3} className="dataTables_empty">
                        No data available in table
                      </td>
                    </tr>
                  ) : (
                    rows.map((app) => (
                      <tr key={num(app, 'ID')} id={'row_' + num(app, 'ID')}>
                        <td className="td-small a-left">{str(app, 'short_name') || <i className="fas fa-minus font-10"></i>}</td>
                        <td className="td-small a-left">
                          {str(app, 'category_name') ? str(app, 'category_name') : <span className="label label-default">No category</span>}
                        </td>
                        <td className="td-small a-left">
                          <form className="changeCategoryForm" action="/settings/softsee/changeApplicationCategory" method="post">
                            <input type="hidden" value={num(app, 'ID')} name="application_id" />
                            {/*
                             * `SelectPicker` CU `form-control`, ca celelalte 95 de
                             * folosiri din v4. Fara ea sertarul iese de 220px si isi
                             * trage eticheta langa el; cu ea tine latimea campului.
                             * Chenarul dublu de care ne temeam nu mai e:
                             * `od_console.css` dezbraca invelisul si pentru
                             * `.bootstrap-select.form-control`, nu doar pentru
                             * `.bootstrap-select.btn-group`.
                             */}
                            <SelectPicker
                              id={'applicationCategory_' + num(app, 'ID')}
                              className="form-control input-sm applicationCategory"
                              name="category"
                              title="Select category..."
                              value={String(num(app, 'category_id'))}
                              onChange={(value) => void onChange(num(app, 'ID'), Number(value))}
                              options={categoryOptions}
                            />
                          </form>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </SoftseeListChrome>
          </div>
        </div>
      </div>
      <Modal
        id="addNewCategory"
        open={open}
        title="New category"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="button" className="btn btn-primary saveCategory" onClick={() => void onSave()}>
              Save
            </button>
          </>
        }
      >
        <form className="form-horizontal labelcustomize" id="newCategoryForm" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="/settings/softsee/newCategory">
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Category name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="category_name" type="text" value={name} onChange={(ev) => setName(ev.target.value)} />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

const TAGS = [
  { id: 0, label: 'Untagged', pill: 'label-default' },
  { id: 1, label: 'Licensed', pill: 'label-info' },
  { id: 2, label: 'Unproductive', pill: 'label-warning' },
  { id: 3, label: 'Suspected', pill: 'label-danger' },
] as const

function TaggingPanel() {
  const query = useQueryClient()
  const [tag, setTag] = useState(0)
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const table = useQuery({
    ...taggingTableQuery({ tag, start, length, search }),
    placeholderData: keepPreviousData,
  })
  if (table.error instanceof ApiError && table.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (table.isPending) return <Spinner />
  const onChange = async (application_id: number, next: number) => {
    try {
      await changeApplicationTag(application_id, next)
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'tagging'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const tagOptions = TAGS.map((t) => ({ value: String(t.id), label: t.label }))
  /* O eticheta noua din coloana reia lista de la prima pagina. */
  const pick = (next: number) => {
    setTag(next)
    setStart(0)
  }
  const rows = table.data?.data ?? []
  return (
    <div id="content">
      {/* Capul zonei, ca pe Inventoree. Ecranul n-are ce crea, deci n-are unealta in cap. */}
      <SettingsHead strong="Software" rest="tagging" subtitle="software tagging system" />
      <div className="row">
        <div className="col-md-3">
          {/*
           * Coloana de alegere a consolei. Inainte erau file `nav-metro-tabs`
           * cu albastrul (65,153,194) scris INLINE pe primul rand - si scris
           * acolo intotdeauna, deci "Untagged" se aprindea si cand era deschis
           * altceva. Acum se aprinde randul chiar ales.
           */}
          <SettingsPicker id="tags-list">
            {TAGS.map((t) => (
              <SettingsPickerItem
                key={t.id}
                label={t.label}
                active={tag === t.id}
                onPick={() => pick(t.id)}
                liProps={{ 'data-tag': t.id }}
              />
            ))}
          </SettingsPicker>
        </div>
        <div className="col-md-9">
          {/*
           * Ca pe Manage applications: eticheta, cautarea si pagina pleaca la
           * server. Ecranul asta era cel mai greu dintre cele trei - cerea
           * intreg catalogul furnizorului printr-o imbinare care citea toata
           * tabela de etichetare pentru fiecare aplicatie.
           */}
          <SoftseeListChrome
            id="softwares-tagging-table"
            search={search}
            onSearch={(next) => {
              setSearch(next)
              setStart(0)
            }}
            start={start}
            length={length}
            total={table.data?.recordsFiltered ?? 0}
            fetching={table.isFetching}
            onStart={setStart}
            onLength={(next) => {
              setLength(next)
              setStart(0)
            }}
          >
            <table id="softwares-tagging-table" className="table table-striped table-hover dataTable softwares-tagging-table">
              {/* Coloanele au latimea lor, deci eticheta si sertarul stau la acelasi x pe toate randurile. */}
              <colgroup>
                <col />
                <col style={{ width: 180 }} />
                <col style={{ width: 260 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="metroTd a-left">Application</th>
                  <th className="metroTd a-left">Tag</th>
                  <th className="metroTd a-left">Change tag</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr className="odd">
                    <td colSpan={3} className="dataTables_empty">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  rows.map((app) => {
                    const current = TAGS.find((t) => t.id === num(app, 'tag_type')) ?? TAGS[0]
                    return (
                      <tr key={num(app, 'ID')}>
                        <td className="metroTd a-left">{str(app, 'short_name') || <i className="fas fa-minus font-10"></i>}</td>
                        <td className="metroTd a-left">
                          {/*
                           * Pastilele zonei: fundal deschis si scris inchis. `label-primary`
                           * nu are regula in `od_console.css`, deci ramanea albastrul lui
                           * Bootstrap cu scris alb - singura pastila din ecran care nu semana
                           * cu celelalte trei. `label-info` e aceeasi vestire, in cerneala zonei.
                           */}
                          <span className={'label ' + current.pill}>{current.label}</span>
                        </td>
                        <td className="metroTd a-left">
                          <form className="changeTagForm" action="/settings/softsee/changeApplicationTag" method="post">
                            <input type="hidden" value={num(app, 'ID')} name="application_id" />
                            <SelectPicker
                              id={'applicationTag_' + num(app, 'ID')}
                              className="form-control input-sm applicationTag"
                              name="tag"
                              title="Select tag..."
                              value={String(num(app, 'tag_type'))}
                              onChange={(value) => void onChange(num(app, 'ID'), Number(value))}
                              options={tagOptions}
                            />
                          </form>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </SoftseeListChrome>
        </div>
      </div>
    </div>
  )
}

const LICENSE_TYPES = [
  [1, 'OEM'],
  [2, 'FPP Retail (Fully Packaged Product)'],
  [3, 'SPLA (Service Provider License Agreement)'],
  [4, 'USL (User Subscription License)'],
  [5, 'VL (Volume Licensing)'],
  [6, 'OVS (Open Value Subscription)'],
  [7, 'GGWA (Get Genuine Windows Agreement)'],
  [8, 'GGK (Get Genuine Kit)'],
] as const

/**
 * Coloana de alegere: felul licentei.
 *
 * In capul listei sta "All licenses" (-1), si de la el porneste ecranul. Portalul
 * vechi pornea de la "No license applications" - adica de la licentele FARA fel -
 * si asta e singurul loc unde ne departam de el dinadins: pe o firma unde totul e
 * licentiat, ecranul se deschidea GOL, si omul trebuia sa ghiceasca singur ca
 * trebuie sa apese un fel de licenta ca sa vada ceva. Manage applications are de
 * mult randul lui "All categories" pentru acelasi motiv.
 *
 * Randul vechi ramane, pe locul si cu intelesul lui: licentele carora nu li s-a
 * pus un fel (0).
 */
const LICENSE_TABS = [
  { id: -1, label: 'All licenses' },
  { id: 0, label: 'No license applications' },
  ...LICENSE_TYPES.map(([id, label]) => ({ id: id as number, label: label as string })),
]

const VALID_PERIODS = [
  { value: '1', label: 'Days' },
  { value: '2', label: 'Months' },
  { value: '3', label: 'Years' },
  { value: '4', label: 'Lifetime' },
]

function LicensesPanel() {
  const query = useQueryClient()
  const page = useQuery(licensesQuery())
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(25)
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  /* Licenta deschisa in fereastra de editare; 0 = niciuna. */
  const [editing, setEditing] = useState(0)
  /* Licenta careia i se vad masinile; 0 = niciuna. */
  const [devicesFor, setDevicesFor] = useState(0)
  const [selected, setSelected] = useState(-1)
  const [licenseType, setLicenseType] = useState('1')
  const [software, setSoftware] = useState('')
  const [period, setPeriod] = useState('1')
  const [startDate, setStartDate] = useState('')
  /* `keepPreviousData`: pagina de acum ramane pe ecran pana vine urmatoarea. */
  const table = useQuery({
    ...licensesTableQuery({ selectedLicense: selected, start, length, search }),
    placeholderData: keepPreviousData,
  })
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    /*
     * Casuta veche avea `required`, deci browserul oprea trimiterea cand data
     * lipsea. `DatePicker` nu primeste `required`, asa ca paza se scrie aici -
     * altfel licenta ar pleca fara data de inceput, iar serverul socoteste din
     * ea data de sfarsit (`licenseEndDate`), care ar iesi goala.
     */
    if (startDate.trim() === '') {
      newAlert('Start date is required.', 'danger')
      return
    }
    try {
      await newLicense(new FormData(event.currentTarget))
      toast('License saved.', '', 'success')
      setOpen(false)
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'licenses'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const rows = table.data?.data ?? []
  /* Un fel nou de licenta din coloana reia lista de la prima pagina. */
  const pick = (next: number) => {
    setSelected(next)
    setStart(0)
  }
  const softwareOptions = page.data.softwares.map((sw) => ({ value: String(num(sw, 'ID')), label: str(sw, 'short_name') }))
  const softwareValue = software || (softwareOptions.length > 0 ? softwareOptions[0].value : '')
  return (
    <>
      <div id="content">
        <SettingsHead
          strong="Software"
          rest="licenses"
          subtitle="software licenses management"
          tools={
            <button type="button" id="odAddLicense" className="pull-right btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add license
            </button>
          }
        />
        <div className="row">
          <div className="col-md-3">
            <SettingsPicker id="licenses-list">
              {LICENSE_TABS.map((tab) => (
                <SettingsPickerItem
                  key={tab.id}
                  label={tab.label}
                  active={tab.id === selected}
                  onPick={() => pick(tab.id)}
                  liProps={{ 'data-license': tab.id }}
                />
              ))}
            </SettingsPicker>
          </div>
          <div className="col-md-9">
            {/*
             * Licentele vin PE PAGINI: felul licentei din coloana, cautarea si
             * pagina pleaca la server. Inainte veneau toate deodata, si fiecare
             * rand isi aducea numarul de masini cu un COUNT al lui - 5.000 de
             * licente insemnau 35.000 de interogari si 15,7 secunde.
             */}
            <SoftseeListChrome
              id="softwares-licenses-table"
              search={search}
              onSearch={(next) => {
                setSearch(next)
                setStart(0)
              }}
              start={start}
              length={length}
              total={table.data?.recordsFiltered ?? 0}
              fetching={table.isFetching}
              onStart={setStart}
              onLength={(next) => {
                setLength(next)
                setStart(0)
              }}
            >
            <table id="softwares-licenses-table" className="table table-striped table-hover table-bordered settingsDataTable softwares-licenses-table">
              <colgroup>
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 190 }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="metroTd a-left">Application</th>
                  <th className="metroTd center">License Type</th>
                  <th className="metroTd center">Licenses</th>
                  <th className="metroTd center">Active licenses</th>
                  <th className="metroTd center">Start date</th>
                  <th className="metroTd center">Status</th>
                  <th className="metroTd center">Document</th>
                  <th className="metroTd center">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr className="odd">
                    <td colSpan={8} className="dataTables_empty">
                      No data available in table
                    </td>
                  </tr>
                )}
                {rows.map((lic) => (
                  <tr key={num(lic, 'ID')}>
                    <td className="metroTd a-left">{str(lic, 'short_name') || <i className="fas fa-minus font-10"></i>}</td>
                    <td className="metroTd center">
                      {str(lic, 'type_name') ? <span className="label label-info">{str(lic, 'type_name')}</span> : <span className="label label-default">Not licensed</span>}
                    </td>
                    <td className="metroTd center">{str(lic, 'licenses_number')}</td>
                    <td className="metroTd center">{str(lic, 'active_licenses')}</td>
                    <td className="metroTd center">{str(lic, 'start_date')}</td>
                    <td className="metroTd center">
                      {str(lic, 'status') === 'expired' ? (
                        <span className="label label-danger">expired</span>
                      ) : (
                        <span className="label label-default">{str(lic, 'end_date')}</span>
                      )}
                    </td>
                    <td className="metroTd center">
                      {str(lic, 'document') ? (
                        <a className="btn btn-sm btn-default" title="Download the document" href={'/api/v2/settings/softsee/licenses/' + num(lic, 'ID') + '/document'}>
                          <i className="fa fa-download"></i>
                        </a>
                      ) : (
                        <i className="fas fa-minus font-10"></i>
                      )}
                    </td>
                    <td className="metroTd center">
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-default odLicenseDevices"
                          title="Devices holding this license"
                          onClick={() => setDevicesFor(num(lic, 'ID'))}
                        >
                          <i className="fas fa-desktop"></i>
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-default odEditLicense"
                          title="License details"
                          onClick={() => setEditing(num(lic, 'ID'))}
                        >
                          <i className="fas fa-pencil-alt"></i>
                        </button>
                        <form className="removeLicenseForm" method="post" action="/settings/softsee/removeLicense" style={{ margin: 0 }}>
                          <input type="hidden" name="license_id" value={num(lic, 'ID')} />
                          <button
                            className="btn btn-sm btn-danger"
                            type="button"
                            onClick={() =>
                              void removeLicense(num(lic, 'ID')).then(() => query.invalidateQueries({ queryKey: ['settings', 'softsee', 'licenses'] }))
                            }
                          >
                            <i className="fas fa-trash"></i> Remove
                          </button>
                        </form>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </SoftseeListChrome>
          </div>
        </div>
      </div>
      <Modal
        id="addNewLicense"
        open={open}
        title="New license"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newLicenseForm" className="btn btn-primary saveLicense">
              Save
            </button>
          </>
        }
      >
        <form className="form-horizontal labelcustomize" id="newLicenseForm" encType="multipart/form-data" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="/settings/softsee/newLicense" onSubmit={(ev) => void onSubmit(ev)}>
          <input type="hidden" name="company" value={page.data.company_id} />
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">License type</span>
            </label>
            <div className="col-sm-9 selectContainer">
              <SelectPicker
                id="license_type"
                className="form-control"
                name="license_type"
                title="Select..."
                value={licenseType}
                onChange={setLicenseType}
                options={LICENSE_TYPES.map(([id, label]) => ({ value: String(id), label: label as string }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software</span>
            </label>
            <div className="col-sm-9 selectContainer">
              <SelectPicker id="license_software" className="form-control" name="license_software" title="Select..." value={softwareValue} onChange={setSoftware} options={softwareOptions} />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">No. of licenses</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="licenses_no" type="number" min={1} defaultValue={1} required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Start date</span>
            </label>
            <div className="col-sm-9">
              {/*
                * `DatePicker`-ul zonei, in locul casutei goale de text. Ea purta
                * `id="datetimepicker"` - carligul cu care portalul vechi agata
                * bootstrap-datetimepicker - dar in v4 nu-l prinde nimeni: ramanea
                * o casuta in care data se scria de mana, fara calendar si fara
                * nicio verificare.
                *
                * Ce pleaca pe sarma nu se schimba: campul se cheama tot
                * `license_start_date` si duce tot `yyyy-mm-dd`, formatul pe care
                * il scria si pickerul vechi (`format: "yyyy-mm-dd"`) si singurul
                * pe care il socoteste `licenseEndDate` din API (`strtotime` pe
                * data de inceput plus perioada).
                *
                * `typed`: casuta ramane scriibila de la tastatura, ca inainte, si
                * calendarul se conduce cu sagetile.
                */}
              <DatePicker
                id="license_start_date"
                name="license_start_date"
                value={startDate}
                onChange={setStartDate}
                placeholder="yyyy-mm-dd"
                typed
              />
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-md-3" style={{ textAlign: 'left' }}>
              <span className="color">Valid license</span>
            </label>
            <div className="col-md-9">
              {/*
               * Numarul si sertarul stau unul langa altul, fiecare cu chenarul
               * lui, in locul `input-group`-ului care le lipea intr-unul singur.
               * Sertarul poarta `form-control`: fara ea iesea de 220px langa o
               * casuta care tine toata latimea, si isi tragea eticheta langa el.
               */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="form-control" name="valid" type="number" style={{ width: 120 }} />
                <span className="color" style={{ margin: 0 }}>
                  Period
                </span>
                <div style={{ flex: '1 1 auto' }}>
                  <SelectPicker id="valid_period" className="form-control" name="valid_period" title="Select..." value={period} onChange={setPeriod} options={VALID_PERIODS} />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Upload document</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="license_document" type="file" />
            </div>
          </div>
        </form>
      </Modal>
      {editing > 0 && <EditLicenseModal id={editing} onClose={() => setEditing(0)} />}
      {devicesFor > 0 && <LicenseDevicesModal id={devicesFor} onClose={() => setDevicesFor(0)} />}
    </>
  )
}

/**
 * "License details": aceleasi campuri ca fereastra de licenta noua, umplute cu
 * licenta aleasa, si salvate prin `updateLicense`
 * (softsee_settings/softwares_licenses/others/editForm.php).
 *
 * PROGRAMUL NU SE SCHIMBA. Nici macheta veche nu-l arata, si `updateLicense`
 * din `DbalSoftseeSettingsSource` nu atinge `application_id`: un sertar de
 * programe aici ar fi aratat o alegere care nu pleaca nicaieri. Numele lui sta
 * scris, ca sa se stie despre ce licenta e vorba.
 *
 * Campurile stau intr-o componenta separata, nu in `LicensesPanel`: citirea
 * licentei e o intrebare pusa serverului pentru licenta ALEASA, iar un `useQuery`
 * de dupa iesirile devreme din panou (`!page.data`) ar schimba numarul de
 * hook-uri de la o desenare la alta.
 */
function EditLicenseModal({ id, onClose }: { id: number; onClose(): void }) {
  const query = useQueryClient()
  const form = useQuery(licenseFormQuery(id))
  const [licenseType, setLicenseType] = useState('')
  const [period, setPeriod] = useState('')
  const [startDate, setStartDate] = useState('')
  /*
   * Data licentei vine de pe server DUPA prima desenare, iar `DatePicker` e
   * condus din afara: se seamana O SINGURA data, cand soseste raspunsul, si de
   * atunci casuta e a omului. Fara semnul asta, fiecare desenare i-ar pune la
   * loc data veche peste ce tocmai a ales.
   */
  const seeded = useRef(false)
  const details = form.data?.license_details
  const detailsDate = details ? str(details, 'start_date') : ''
  useEffect(() => {
    if (seeded.current || detailsDate === '') return
    seeded.current = true
    setStartDate(detailsDate)
  }, [detailsDate])
  /* Cat timp omul n-a umblat la sertar, arata ce scrie in licenta. */
  const typeValue = licenseType || (details ? String(num(details, 'license_type')) : '')
  const periodValue = period || (details ? String(num(details, 'valid_period')) : '')
  const docName = details ? str(details, 'document') : ''
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    /*
     * `DatePicker` nu primeste `required`, ca si la licenta noua: paza se scrie
     * aici. Fara data de inceput, `licenseEndDate()` din API socoteste o data de
     * sfarsit goala, si licenta ar ramane fara termen.
     */
    if (startDate.trim() === '') {
      newAlert('Start date is required.', 'danger')
      return
    }
    try {
      await updateLicense(new FormData(event.currentTarget))
      toast('License saved.', '', 'success')
      onClose()
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'licenses'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  return (
    <Modal
      id="editLicense"
      open={true}
      title="License details"
      labelledBy="editLicenseLabel"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>
          <button type="submit" form="editLicenseForm" className="btn btn-primary updateLicense" disabled={!details}>
            Update
          </button>
        </>
      }
    >
      {form.error instanceof ApiError && form.error.status === 403 ? (
        <div className="alert alert-danger">You do not have access to this action.</div>
      ) : !details ? (
        <Spinner />
      ) : (
        <form
          className="form-horizontal labelcustomize updateLicenseForm"
          id="editLicenseForm"
          encType="multipart/form-data"
          data-collabel="3"
          data-alignlabel="left"
          data-label="color"
          method="post"
          action="/settings/softsee/updateLicense"
          onSubmit={(ev) => void onSubmit(ev)}
        >
          <input type="hidden" name="license" value={id} />
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software</span>
            </label>
            <div className="col-sm-9">
              <p className="form-control-static">{str(details, 'short_name') || <i className="fas fa-minus font-10"></i>}</p>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">License type</span>
            </label>
            <div className="col-sm-9 selectContainer">
              <SelectPicker
                id="edit_license_type"
                className="form-control"
                name="license_type"
                title="Select..."
                value={typeValue}
                onChange={setLicenseType}
                options={LICENSE_TYPES.map(([value, label]) => ({ value: String(value), label: label as string }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">No. of licenses</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="licenses_no" type="number" min={1} defaultValue={str(details, 'licenses_number')} required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Start date</span>
            </label>
            <div className="col-sm-9">
              {/* Acelasi calendar ca la licenta noua, cu `typed`: se scrie si de la tastatura. */}
              <DatePicker
                id="edit_license_start_date"
                name="license_start_date"
                value={startDate}
                onChange={setStartDate}
                placeholder="yyyy-mm-dd"
                typed
              />
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-md-3" style={{ textAlign: 'left' }}>
              <span className="color">Valid license</span>
            </label>
            <div className="col-md-9">
              {/* Aceeasi asezare ca la licenta noua: numarul si sertarul, fiecare cu chenarul lui. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="form-control" name="valid" type="number" style={{ width: 120 }} defaultValue={str(details, 'valid')} />
                <span className="color" style={{ margin: 0 }}>
                  Period
                </span>
                <div style={{ flex: '1 1 auto' }}>
                  <SelectPicker
                    id="edit_valid_period"
                    className="form-control"
                    name="valid_period"
                    title="Select..."
                    value={periodValue}
                    onChange={setPeriod}
                    options={VALID_PERIODS}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Upload document</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="license_document" type="file" />
              {docName !== '' && (
                <p className="help-block" style={{ marginBottom: 0 }}>
                  <i className="fa fa-paperclip" aria-hidden="true"></i>{' '}
                  <a href={'/api/v2/settings/softsee/licenses/' + id + '/document'}>{docName}</a>
                </p>
              )}
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}

/**
 * "License devices": masinile care TIN licenta, si masinile pe care se mai
 * POATE pune - cele doua file din portal (`others/licenseDevices.php` si
 * `others/addLicenseDevice.php`, legate de `actionTabs.php`), aduse intr-o
 * singura fereastra, fiindca sunt doua jumatati ale aceleiasi intrebari.
 *
 * Capetele erau scrise si nefolosite in `SoftseeSettings`: `licenseDevices`,
 * `addLicenseDevice`, `assignDevicesLicense`, `removeDeviceLicense`.
 *
 * X-ul de pe rand trimite ID-ul LEGATURII (`it_softsee_device_licenses.ID`), nu
 * al masinii - asa cere capatul, si asa trimitea si macheta veche. De-aia
 * randul poarta `data-assignment`, nu `data-device`.
 */
function LicenseDevicesModal({ id, onClose }: { id: number; onClose(): void }) {
  const query = useQueryClient()
  const held = useQuery(licenseDevicesQuery(id))
  const free = useQuery(licenseDevicesAvailableQuery(id))
  const [picked, setPicked] = useState<string[]>([])
  const forbidden =
    (held.error instanceof ApiError && held.error.status === 403) ||
    (free.error instanceof ApiError && free.error.status === 403)
  const devices = held.data?.devices ?? []
  const available = free.data?.devices ?? []
  const left = free.data?.leftLicenses ?? 0
  /*
   * Dupa fiecare scriere se reimprospateaza si LISTA DE LICENTE: coloana
   * "Active licenses" de pe ecran numara chiar legaturile astea, deci ar fi
   * ramas in urma cu o masina.
   */
  const refresh = async () => {
    await Promise.all([
      query.invalidateQueries({ queryKey: ['settings', 'softsee', 'licenses', 'devices', id] }),
      query.invalidateQueries({ queryKey: ['settings', 'softsee', 'licenses', 'devices', 'available', id] }),
      query.invalidateQueries({ queryKey: ['settings', 'softsee', 'licenses'] }),
    ])
  }
  const onAssign = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (picked.length === 0) {
      newAlert('Pick at least one device.', 'danger')
      return
    }
    try {
      await assignDevicesLicense(id, picked)
      toast('License applied.', '', 'success')
      setPicked([])
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  const onRemove = async (assignmentId: number) => {
    /* Aceeasi intrebare ca in portal, inainte de a scoate licenta de pe masina. */
    if (!window.confirm('Are you sure that you want to remove this license?')) return
    try {
      await removeDeviceLicense(assignmentId)
      toast('License removed.', '', 'success')
      await refresh()
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  return (
    <Modal
      id="licenseDevices"
      open={true}
      large
      title="License devices"
      labelledBy="licenseDevicesLabel"
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-default" onClick={onClose}>
          Close
        </button>
      }
    >
      {forbidden ? (
        <div className="alert alert-danger">You do not have access to this action.</div>
      ) : !held.data || !free.data ? (
        <Spinner />
      ) : (
        <>
          <table className="table table-striped table-hover table-bordered settingsDataTable license-devices-table">
            <colgroup>
              <col />
              <col style={{ width: 260 }} />
              <col style={{ width: 90 }} />
            </colgroup>
            <thead>
              <tr>
                <th className="metroTd a-left">Device</th>
                <th className="metroTd center">User</th>
                <th className="metroTd center">Action</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => (
                <tr key={num(device, 'ID')} data-assignment={num(device, 'ID')}>
                  <td className="metroTd a-left">
                    <i className="fas fa-desktop" aria-hidden="true"></i> {str(device, 'Denumire') || <i className="fas fa-minus font-10"></i>}
                  </td>
                  <td className="metroTd center">
                    {str(device, 'UserLogat') ? (
                      <span className="label label-default">{str(device, 'UserLogat')}</span>
                    ) : (
                      <i className="fas fa-minus font-10"></i>
                    )}
                  </td>
                  <td className="metroTd center">
                    <button
                      type="button"
                      className="btn btn-sm btn-danger odRemoveDeviceLicense"
                      title="Take the license off this device"
                      onClick={() => void onRemove(num(device, 'ID'))}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </td>
                </tr>
              ))}
              {devices.length === 0 && (
                <tr>
                  <td className="metroTd a-left" colSpan={3}>
                    <i className="fas fa-info-circle" aria-hidden="true"></i> No devices with license.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <hr />
          {left < 1 ? (
            <div className="alert alert-danger" id="noLicensesLeft">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i> No licenses available.
            </div>
          ) : (
            <form
              className="form-horizontal labelcustomize assignLicenseForm"
              id="assignLicenseForm"
              data-collabel="3"
              data-alignlabel="left"
              data-label="color"
              method="post"
              action={'/settings/softsee/assignDevicesLicense'}
              onSubmit={(ev) => void onAssign(ev)}
            >
              <input type="hidden" name="license" value={id} />
              <div className="alert alert-info" id="licensesLeft">
                <i className="fas fa-info-circle" aria-hidden="true"></i> {left} licenses available. Pick more machines than
                that and only the first {left} are taken.
              </div>
              <div className="form-group">
                <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
                  <span className="color">Devices</span>
                </label>
                <div className="col-sm-9 selectContainer">
                  <SelectPicker
                    id="license_devices"
                    className="form-control"
                    name="license_devices[]"
                    title="Select ..."
                    multiple
                    size={10}
                    selectedTextFormat="count > 3"
                    value={picked}
                    onChange={setPicked}
                    options={available.map((device) => ({
                      value: String(num(device, 'ID')),
                      label: str(device, 'Denumire'),
                      subtext: str(device, 'UserLogat') ? '(' + str(device, 'UserLogat') + ')' : undefined,
                    }))}
                  />
                </div>
              </div>
              <div className="form-group offset">
                <div className="col-md-offset-3 col-md-9">
                  <button type="submit" className="btn btn-primary saveDeviceLicense">
                    Save
                  </button>
                </div>
              </div>
            </form>
          )}
        </>
      )}
    </Modal>
  )
}

function KitsPanel() {
  const query = useQueryClient()
  const page = useQuery(kitsQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(0)
  /* Sertarele consolei sunt controlate, deci valorile lor stau aici. Categoria
     si pachetul se seamana cu PRIMUL rand, fiindca asta alegea si `<select>`-ul
     brut cand nimeni nu-i dadea o valoare. `<select>`-ul ascuns al fiecarui
     sertar ramane in formular, deci `FormData` pleaca neschimbat. */
  const [category, setCategory] = useState('')
  const [pack, setPack] = useState('')
  const [os, setOs] = useState<string[]>([])
  useEffect(() => {
    if (!page.data) return
    const firstCategory = page.data.app_categories[0]
    const firstPack = page.data.packs[0]
    setCategory(firstCategory ? String(num(firstCategory, 'ID')) : '')
    setPack(firstPack ? String(num(firstPack, 'ID')) : '')
  }, [page.data])
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      await addKit(new FormData(event.currentTarget))
      toast('Kit saved.', '', 'success')
      setOpen(false)
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'kits'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  return (
    <>
      <div id="content">
        {/*
         * Capul zonei: `h4` de 22px si unealta plutita la dreapta INAINTEA
         * titlului. Inainte era `h3` de 26px, iar butonul statea intr-o bara
         * plutita DUPA subtitlu, de unde cobora peste capul tabelului.
         */}
        <SettingsHead
          strong="Manage"
          rest="kits"
          subtitle="list of current software kits"
          tools={
            <button type="button" className="btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add kit
            </button>
          }
        />
        {page.data.allowed ? (
          <table className="table table-striped table-hover" id="kits_list_table">
            <tbody>
              {page.data.kits.map((kit) => (
                <tr key={num(kit, 'ID')}>
                  <td>{str(kit, 'name')}</td>
                  <td>{str(kit, 'version')}</td>
                  <td>
                    <div className="softsee-row-actions">
                      <button type="button" className="btn btn-sm btn-default odEditKit" title="Kit details" onClick={() => setEditing(num(kit, 'ID'))}>
                        <i className="fas fa-pencil-alt"></i> Edit
                      </button>
                      <form className="deleteKit" method="post" action="/settings/softsee/removeKit">
                        <input type="hidden" name="software" value={num(kit, 'ID')} />
                        <button className="btn btn-sm btn-danger" type="button" onClick={() => void removeKit(num(kit, 'ID')).then(() => query.invalidateQueries({ queryKey: ['settings', 'softsee', 'kits'] }))}>
                          <i className="fas fa-trash"></i> Delete
                        </button>
                      </form>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="alert alert-warning">
            <i className="fa fa-ban"></i> Access denied.
          </div>
        )}
      </div>
      {editing > 0 && <EditKitModal id={editing} onClose={() => setEditing(0)} />}
      {/* Fereastra comuna a zonei, ca la Create Company. */}
      <Modal
        id="myKitModal"
        open={open}
        title="Add new kit"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newKitForm" className="btn btn-primary goSaveKit">
              <i className="far fa-save"></i> Save
            </button>
          </>
        }
      >
        <form className="form-horizontal labelcustomize" id="newKitForm" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="/settings/softsee/addKit" onSubmit={(ev) => void onSubmit(ev)}>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <button className="btn btn-primary btn-xs add_msi" type="button">
                <i className="fas fa-plus"></i> Upload MSI
              </button>
            </label>
            <div className="col-sm-9">
              <span className="file-name" style={{ padding: 10 }}></span>
              <input type="file" style={{ visibility: 'hidden' }} id="upload_msi" name="msi" />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="kit_name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software version</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="kit_version" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Category</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newKitCategory"
                className="form-control"
                name="kit_category"
                title="Select.."
                value={category}
                onChange={setCategory}
                options={page.data.app_categories.map((cat) => ({ value: String(num(cat, 'ID')), label: str(cat, 'category_name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Pack</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newKitPack"
                className="form-control"
                name="kit_pack"
                title="Select.."
                value={pack}
                onChange={setPack}
                options={page.data.packs.map((row) => ({ value: String(num(row, 'ID')), label: str(row, 'pack_name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Exe</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="kit_exe" type="text" />
              <span className="help-block">
                <i className="fas fa-info-circle"></i> Executable to close when the script is ran
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Params</span>
            </label>
            <div className="col-sm-9">
              <textarea style={{ maxWidth: '100%' }} className="form-control" name="kit_params"></textarea>
              <span className="help-block">
                <i className="fas fa-info-circle"></i> /qn Reboot=ReallySuppress AllUsers=1
              </span>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Operating system</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newKitOs"
                className="form-control"
                name="kit_os[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={os}
                onChange={setOs}
                options={page.data.operating_systems.map((row) => ({ value: String(num(row, 'ID')), label: osLabel(row) }))}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}

function CustomKitsPanel() {
  const query = useQueryClient()
  const page = useQuery(customKitsQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(0)
  /* Sertarele consolei sunt controlate, deci valorile lor stau aici. Firma si
     categoria se seamana cu ce arata serverul; `<select>`-ul ascuns al fiecarui
     sertar ramane in formular, deci `FormData` pleaca neschimbat. */
  const [company, setCompany] = useState('')
  const [category, setCategory] = useState('')
  const [os, setOs] = useState<string[]>([])
  const [packages, setPackages] = useState<string[]>([])
  useEffect(() => {
    if (!page.data) return
    setCompany(String(num(page.data as unknown as Record<string, unknown>, 'company_id') || ''))
    const first = page.data.categories[0]
    setCategory(first ? String(num(first, 'ID')) : '')
  }, [page.data])
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      await addCustomKit(new FormData(event.currentTarget))
      toast('Kit saved.', '', 'success')
      setOpen(false)
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'custom-kits'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  return (
    <>
      <div id="content">
        {/*
         * Capul zonei: `h4` de 22px si unealta plutita la dreapta INAINTEA
         * titlului. Inainte era `h3` de 26px, iar butonul statea intr-o bara
         * plutita DUPA subtitlu, de unde cobora peste capul tabelului.
         */}
        <SettingsHead
          strong="Manage"
          rest="custom kits"
          subtitle="list of current custom software kits"
          tools={
            <button type="button" className="btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add custom kit
            </button>
          }
        />
        <table className="table table-striped table-hover" id="softsee_custom_kits" data-company={page.data.company_id}>
          <tbody>
            {page.data.kits.map((kit) => (
              <tr key={num(kit, 'ID')}>
                <td>{str(kit, 'Name')}</td>
                <td>{str(kit, 'Version')}</td>
                <td>
                  <div className="softsee-row-actions">
                    <button type="button" className="btn btn-sm btn-default odEditCustomKit" title="Custom kit details" onClick={() => setEditing(num(kit, 'ID'))}>
                      <i className="fas fa-pencil-alt"></i> Edit
                    </button>
                    <form className="deleteCustomKit" method="post" action="/settings/softsee/deleteCustomKit">
                      <input type="hidden" name="ID" value={num(kit, 'ID')} />
                      <button className="btn btn-sm btn-danger" type="button" onClick={() => void deleteCustomKit(num(kit, 'ID')).then(() => query.invalidateQueries({ queryKey: ['settings', 'softsee', 'custom-kits'] }))}>
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </form>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Fereastra comuna a zonei, ca la Create Company: `BsModal` scris de mana
          nu punea valul din spate, nici `body.modal-open`, si nu asculta Escape. */}
      {editing > 0 && <EditCustomKitModal id={editing} onClose={() => setEditing(0)} />}
      <Modal
        id="addCustomKitForm"
        open={open}
        title="Add new custom kit"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newCustomKitForm" className="btn btn-primary saveCustomKit">
              <i className="far fa-save"></i> Save
            </button>
          </>
        }
      >
        <form className="form-horizontal labelcustomize" id="newCustomKitForm" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="/settings/softsee/addCustomKit" onSubmit={(ev) => void onSubmit(ev)}>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Software version</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="version" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Company</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitCompany"
                className="form-control"
                name="company"
                title="Select.."
                value={company}
                onChange={setCompany}
                options={page.data.companies.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'Nume') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Category</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitCategory"
                className="form-control"
                name="category"
                title="Select.."
                value={category}
                onChange={setCategory}
                options={page.data.categories.map((cat) => ({ value: String(num(cat, 'ID')), label: str(cat, 'category_name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Exe</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="exe_to_kill" type="text" />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Params</span>
            </label>
            <div className="col-sm-9">
              <textarea style={{ maxWidth: '100%' }} className="form-control" name="parameters"></textarea>
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Operating system</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitOs"
                className="form-control"
                name="os[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={os}
                onChange={setOs}
                options={page.data.operating_systems.map((row) => ({ value: String(num(row, 'ID')), label: osLabel(row) }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Add to Package</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomKitPackages"
                className="form-control"
                name="packages[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                size={10}
                value={packages}
                onChange={setPackages}
                options={(page.data.packages ?? []).map((pkg) => ({ value: String(num(pkg, 'ID')), label: str(pkg, 'Name') }))}
              />
            </div>
          </div>
          <input type="file" style={{ visibility: 'hidden' }} id="upload_msi" name="msi" />
        </form>
      </Modal>
    </>
  )
}

function CustomPackagesPanel() {
  const query = useQueryClient()
  const page = useQuery(customPackagesQuery())
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(0)
  /* Sertarele consolei sunt controlate, deci valoarea lor sta aici, nu in
     `defaultValue`. Firma se seamana cu cea aleasa in bara de sus. */
  const [company, setCompany] = useState('')
  const [customKits, setCustomKits] = useState<string[]>([])
  const [standardKits, setStandardKits] = useState<string[]>([])
  useEffect(() => {
    if (page.data) setCompany(String(num(page.data as unknown as Record<string, unknown>, 'company_id') || ''))
  }, [page.data])
  if (page.error instanceof ApiError && page.error.status === 403) {
    return <div className="alert alert-danger">You do not have access to this action.</div>
  }
  if (!page.data) return <Spinner />
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    try {
      await addCustomPackage({
        name: (form.elements.namedItem('name') as HTMLInputElement).value,
        description: (form.elements.namedItem('description') as HTMLInputElement).value,
        company,
        /* Kiturile alese pleaca si ele: addingPackage le trece prin
           addKitToPackage. Pana pe 18 septembrie 2026 un pachet facut de aici
           iesea intotdeauna gol. */
        custom_kits: customKits,
        standard_kits: standardKits,
      })
      toast('Package saved.', '', 'success')
      setOpen(false)
      await query.invalidateQueries({ queryKey: ['settings', 'softsee', 'custom-packages'] })
    } catch (err) {
      newAlert(err instanceof Error ? err.message : 'Error', 'danger')
    }
  }
  return (
    <>
      <div id="content">
        {/*
         * Capul zonei, ca pe celelalte ecrane de setari: `h4` de 22px si
         * unealta plutita la dreapta INAINTEA titlului. Inainte era `h3` de
         * 26px, iar butonul statea intr-o bara plutita DUPA subtitlu, de unde
         * cobora peste capul tabelului.
         */}
        <SettingsHead
          strong="Manage"
          rest="custom packages"
          subtitle="list of current custom packages"
          tools={
            <button type="button" className="btn btn-success btn-sm" onClick={() => setOpen(true)}>
              <i className="fas fa-plus-circle" aria-hidden="true"></i> Add custom package
            </button>
          }
        />
        <table className="table table-striped table-hover" id="softsee_custom_packages" data-company={page.data.company_id}>
          <tbody>
            {page.data.packages.map((pkg) => (
              <tr key={num(pkg, 'ID')}>
                <td>{str(pkg, 'Name')}</td>
                <td>{str(pkg, 'Description')}</td>
                <td>
                  <div className="softsee-row-actions">
                    <button type="button" className="btn btn-sm btn-default odEditCustomPackage" title="Package details" onClick={() => setEditing(num(pkg, 'ID'))}>
                      <i className="fas fa-pencil-alt"></i> Edit
                    </button>
                    <form className="deleteCustomPackage" method="post" action="/settings/softsee/deleteCustomPackage">
                      <input type="hidden" name="ID" value={num(pkg, 'ID')} />
                      <button className="btn btn-sm btn-danger" type="button" onClick={() => void deleteCustomPackage(num(pkg, 'ID')).then(() => query.invalidateQueries({ queryKey: ['settings', 'softsee', 'custom-packages'] }))}>
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </form>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Fereastra comuna a zonei, ca la Create Company: `BsModal` scris de mana
          nu punea valul din spate, nu punea `body.modal-open` si nu asculta Escape. */}
      {editing > 0 && <EditCustomPackageModal id={editing} onClose={() => setEditing(0)} />}
      <Modal
        id="addCustomPackageForm"
        open={open}
        title="Add new custom package"
        onClose={() => setOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-default" onClick={() => setOpen(false)}>
              Close
            </button>
            <button type="submit" form="newCustomPackageForm" className="btn btn-primary">
              Save
            </button>
          </>
        }
      >
        <form className="form-horizontal labelcustomize" id="newCustomPackageForm" data-collabel="3" data-alignlabel="left" data-label="color" method="post" action="/settings/softsee/addCustomPackage" onSubmit={(ev) => void onSubmit(ev)}>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Package name</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="name" type="text" required />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Description</span>
            </label>
            <div className="col-sm-9">
              <input className="form-control" name="description" type="text" />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Company</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomPackageCompany"
                className="form-control"
                name="company"
                title="Select.."
                value={company}
                onChange={setCompany}
                options={page.data.companies.map((c) => ({ value: String(num(c, 'ID')), label: str(c, 'Nume') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Add Custom Kits (*)</span>
            </label>
            <div className="col-sm-9">
              {/*
               * `required` a cazut de pe cele doua sertare de kituri, si nu din
               * neglijenta: `SelectPicker` tine `<select>`-ul adevarat ascuns,
               * iar un camp ascuns si `required` opreste trimiterea cu "not
               * focusable", fara sa spuna omului nimic. Poarta oricum nu pazea
               * nimic. Kiturile alese pleaca acum cu pachetul (18 septembrie
               * 2026); inainte se trimiteau doar numele, descrierea si firma.
               */}
              <SelectPicker
                id="newCustomPackageCustomKits"
                className="form-control"
                name="custom_kits[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                value={customKits}
                onChange={setCustomKits}
                size={10}
                options={(page.data.custom_kits ?? []).map((kit) => ({ value: String(num(kit, 'ID')), label: str(kit, 'Name') }))}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="col-sm-3 control-label" style={{ textAlign: 'left' }}>
              <span className="color">Add Standard Kits (*)</span>
            </label>
            <div className="col-sm-9">
              <SelectPicker
                id="newCustomPackageStandardKits"
                className="form-control"
                name="standard_kits[]"
                title="Select.."
                multiple
                selectedTextFormat="count > 2"
                value={standardKits}
                onChange={setStandardKits}
                size={10}
                options={(page.data.main_kits ?? []).map((kit) => ({ value: String(num(kit, 'ID')), label: str(kit, 'name') }))}
              />
            </div>
          </div>
        </form>
      </Modal>
    </>
  )
}
